/**
 * Error type definitions and type guards
 */

/**
 * Standard API error structure
 */
export interface ApiError extends Error {
  message: string;
  code?: string;
  statusCode?: number;
  details?: unknown;
}

/**
 * Office.js specific error
 */
export interface OfficeError extends Error {
  message: string;
  name: string;
  code?: string;
  traceMessages?: string[];
  innerError?: Error;
  debugInfo?: {
    errorLocation?: string;
  };
}

/**
 * Generic error with message property guaranteed
 */
export interface ErrorWithMessage {
  message: string;
  [key: string]: unknown;
}

/**
 * Type guard for ApiError
 */
export const isApiError = (error: unknown): error is ApiError => {
  return (
    error instanceof Error && "message" in error && typeof (error as ApiError).message === "string"
  );
};

/**
 * Type guard for OfficeError
 */
export const isOfficeError = (error: unknown): error is OfficeError => {
  return error instanceof Error && "message" in error && "name" in error;
};

/**
 * Type guard for any error with a message
 */
export const hasErrorMessage = (error: unknown): error is ErrorWithMessage => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ErrorWithMessage).message === "string"
  );
};

/**
 * Safe error message extraction
 */
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  if (hasErrorMessage(error)) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unknown error occurred";
};

/**
 * Format error for display
 */
export const formatError = (error: unknown): string => {
  const message = getErrorMessage(error);

  if (isApiError(error) && error.code) {
    return `${message} (Code: ${error.code})`;
  }

  if (isOfficeError(error) && error.code) {
    return `${message} (Office Error: ${error.code})`;
  }

  return message;
};
